import { useEffect } from "react";
import { motion } from "framer-motion";
import SIPCalculator from "@/components/calculators/SIPCalculator";
import LumpsumCalculator from "@/components/calculators/LumpsumCalculator";
import GoalPlannerCalculator from "@/components/calculators/GoalPlannerCalculator";
import StepUpSIPCalculator from "@/components/calculators/StepUpSIPCalculator";
import { Calculator, Target, TrendingUp, Wallet } from "lucide-react";

const SECTIONS = [
  { id: "sip", icon: Calculator, title: "SIP Calculator", desc: "How a steady monthly investment grows over time.", Component: SIPCalculator },
  { id: "lumpsum", icon: Wallet, title: "Lumpsum Calculator", desc: "A one-time investment, compounding silently.", Component: LumpsumCalculator },
  { id: "goal", icon: Target, title: "Goal Planner", desc: "Tell us your dream — we'll back-calculate the SIP.", Component: GoalPlannerCalculator },
  { id: "stepup", icon: TrendingUp, title: "Step-up SIP", desc: "Grow your SIP every year, just like your salary.", Component: StepUpSIPCalculator },
];

export default function CalculatorsPage() {
  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="bg-background">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10">
        <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium">Calculators</span>
        <h1 className="font-serif text-5xl md:text-6xl text-primary mt-3 leading-[1.05] tracking-tight">
          Run the numbers. <em className="not-italic text-secondary">Then run after them.</em>
        </h1>
        <p className="mt-6 text-lg text-foreground/75 max-w-2xl leading-relaxed">
          Four calculators, one belief: financial decisions get easier when you see them visually.
          Play with the inputs — there's no wrong answer.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-24 space-y-16">
        {SECTIONS.map((s, i) => (
          <motion.section
            key={s.id}
            id={s.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5 }}
            className="bg-card rounded-3xl border border-border p-6 md:p-10 shadow-sm scroll-mt-28"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center">
                <s.icon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-serif text-3xl text-primary">{s.title}</h2>
                <p className="text-sm text-foreground/65">{s.desc}</p>
              </div>
            </div>
            <s.Component />
          </motion.section>
        ))}
      </div>
    </div>
  );
}
